import React from "react";
import {
  type DataPoint,
  type MetricFormatterFunction,
} from "@/src/features/widgets/chart-library/chart-props";
import {
  formatMetric,
  toFullMetricString,
} from "@/src/features/widgets/chart-library/utils";
import { BarChart, Bar, XAxis, YAxis } from "recharts";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/src/components/ui/chart";

interface HistogramDataPoint {
  binLabel: string;
  count: number;
  lower?: number;
  upper?: number;
  height?: number;
}

const HistogramChart = ({
  data,
  config = {
    count: {
      label: "Count",
      color: "hsl(var(--chart-1))",
    },
  },
  subtleFill = false,
  metricFormatter = (value, options) => formatMetric(value, options),
}: {
  data: DataPoint[];
  config?: ChartConfig;
  subtleFill?: boolean;
  metricFormatter?: MetricFormatterFunction;
}) => {
  const formatBinEdge = (value: number) =>
    toFullMetricString(metricFormatter(value, { style: "compact" }));

  const transformHistogramData = (data: DataPoint[]): HistogramDataPoint[] => {
    if (!data.length) return [];

    // Check if this is ClickHouse histogram format (array of tuples)
    const firstDataPoint = data[0];
    if (firstDataPoint?.metric && Array.isArray(firstDataPoint.metric)) {
      // ClickHouse histogram format: [(lower, upper, height), ...]
      return (firstDataPoint.metric as [number, number, number][]).map(
        ([lower, upper, height]) => ({
          binLabel: `[${formatBinEdge(lower)}, ${formatBinEdge(upper)}]`,
          count: height,
          lower,
          upper,
          height,
        }),
      );
    }

    // Fallback: treat as regular data points with binLabel
    return data.map((item) => ({
      binLabel: item.dimension || `Bin ${data.indexOf(item) + 1}`,
      count: (item.metric as number) || 0,
    }));
  };

  const histogramData = transformHistogramData(data);

  if (!histogramData.length) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center">
        No data available
      </div>
    );
  }

  return (
    <ChartContainer
      config={config}
      className="[&_.recharts-bar-rectangle:hover]:opacity-30 dark:[&_.recharts-bar-rectangle:hover]:opacity-100 dark:[&_.recharts-bar-rectangle:hover]:brightness-[3]"
    >
      <BarChart
        data={histogramData}
        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
      >
        <XAxis
          dataKey="binLabel"
          stroke="hsl(var(--chart-grid))"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          angle={-45}
          textAnchor="end"
          height={90}
        />
        <YAxis
          stroke="hsl(var(--chart-grid))"
          fontSize={12}
          tickLine={false}
          axisLine={false}
          niceTicks="auto"
        />
        <Bar
          dataKey="count"
          fill="hsl(var(--chart-1))"
          radius={[2, 2, 0, 0]}
          fillOpacity={subtleFill ? 0.3 : 1}
          isAnimationActive={false}
        />
        <ChartTooltip
          cursor={false}
          contentStyle={{ backgroundColor: "hsl(var(--background))" }}
          content={({ active, payload, label }) => (
            <ChartTooltipContent
              active={active}
              payload={payload}
              label={label}
              valueFormatter={(v) =>
                toFullMetricString(
                  formatMetric(Number(v), { style: "compact" }),
                )
              }
              nameFormatter={(name) => (name === "count" ? "Count" : name)}
              labelFormatter={(label) => `Bin: ${label}`}
            />
          )}
        />
      </BarChart>
    </ChartContainer>
  );
};

export default HistogramChart;
